/**
 * L'enveloppe de session du Studio, et le compteur des gestes qui s'y répètent.
 *
 * # Ce qu'elle répare
 *
 * Le Studio hors parcours n'avait **aucune** session. Le jeu libre — l'une des
 * trois portes de l'écran d'accueil — était muet dès qu'on posait les doigts
 * sur le clavier ; appuyer sur lecture ne laissait pas de trace ; cliquer dans
 * la minimap, choisir une mesure, changer le tempo, armer une boucle non plus.
 * Le dénominateur de tout l'usage du Studio manquait, si bien qu'un visiteur
 * resté une demi-heure à jouer et un visiteur reparti aussitôt se
 * ressemblaient trait pour trait.
 *
 * # Pourquoi un agrégat
 *
 * Ce sont des gestes répétitifs, et la règle de la maison interdit de les
 * émettre à l'unité : un événement par clic de minimap serait un compteur de
 * clics, facturé. Ils sont donc comptés en mémoire et résumés une fois, à la
 * fermeture, exactement comme `practice_session_ended` résume déjà les phrases
 * d'un parcours.
 *
 * # Pourquoi un module et pas un hook
 *
 * Le Studio est monté en permanence et les morceaux s'y succèdent sans
 * démontage : il n'y a pas de cycle de vie de composant sur lequel s'accrocher.
 * C'est le même choix, pour la même raison, que
 * {@link import("./exercise-session")}.
 *
 * # Ce qui n'est pas compté, et pourquoi
 *
 * Les lectures déclenchées par l'application elle-même — jingle d'accueil,
 * pré-écoute d'un accord, passe automatique d'un parcours, retour de boucle —
 * ne sont pas des lectures : les compter ferait de `plays` un compteur
 * d'animations. Seuls les appels passés par {@link recordPlay} en sont, et ce
 * sont les commandes de transport de l'interface qui les posent.
 *
 * # Deux compteurs, deux sources, et c'est délibéré
 *
 * `plays` compte des **intentions** et se pose sur la commande ; `playedMs`
 * compte du **son** et se pose sur l'événement du transport
 * ({@link recordTransportStarted}). Les avoir posés au même endroit arrêtait le
 * chronomètre définitivement au premier geste de travail : un clic de minimap,
 * un glissé du rouleau et un retour de boucle mettent tous le transport en
 * pause puis le relancent d'eux-mêmes, sans passer par un bouton. Le temps
 * joué se dégradait donc d'autant plus que la personne travaillait.
 */
import { enqueue } from "./client";
import { resumableOnDeparture } from "./departure";
import type { StudioContextType, VirtuosaEvent } from "./events";

interface StudioSession {
  context: StudioContextType;
  pieceId?: string;
  exerciseId?: string;
  /**
   * Objet qui « possède » la session. Comparer les identités permet de voir
   * que le Studio est passé à autre chose sans câbler une sortie sur chacun
   * des chemins de chargement. Même patron que `exercise-session`.
   */
  owner: object | null;
  startedAt: number;
  plays: number;
  /** Cumul du temps de transport en marche, hors segment en cours. */
  playedMs: number;
  /** Instant du dernier départ, ou `0` quand le transport est à l'arrêt. */
  playingSince: number;
  notesPlayed: number;
  midiUsed: boolean;
  tempoChanges: number;
  /** Fin de la dernière rafale de tempo : un glissé de curseur compte pour un. */
  lastTempoChangeAt: number;
  seeks: number;
  sectionPicks: number;
  loops: number;
  metronomeUsed: boolean;
  countIns: number;
}

/**
 * Deux changements de tempo plus rapprochés que ça appartiennent au même
 * geste. Le curseur et la répétition au maintien en produisent des dizaines
 * par seconde ; sans ce regroupement, `tempoChanges` mesurerait la sensibilité
 * du contrôle, pas l'intention de ralentir.
 */
const TEMPO_BURST_MS = 800;

let current: StudioSession | null = null;

/** Ce qu'il faut pour rouvrir la même session au retour au premier plan. */
interface StudioSubject {
  readonly context: StudioContextType;
  readonly options: { owner?: object | null; pieceId?: string; exerciseId?: string };
}

/**
 * Le départ, le retour, et la mémoire du sujet entre les deux.
 *
 * Le sujet est oublié dès que la session se ferme pour une raison qui n'est pas
 * un départ. Sans cet oubli, quitter le Studio puis passer l'application en
 * arrière-plan depuis la bibliothèque rouvrait une session de Studio au retour,
 * dont la durée comptait le temps passé ailleurs.
 */
const departure = resumableOnDeparture<StudioSubject>({
  close: () => {
    endStudioSession("pagehide");
  },
  reopen: (subject) => {
    startStudioSession(subject.context, subject.options);
  },
});

function emit(event: VirtuosaEvent): void {
  // Passe par `enqueue` et non par le `track` de `index.ts` : ce module est
  // importé par la barrière publique, y remonter créerait un cycle.
  enqueue(event);
}

/**
 * Ouvre une session, en fermant la précédente s'il y en avait une.
 *
 * Idempotent sur un même contexte : réappeler avec le même `owner` et le même
 * contexte ne coupe pas la session en cours. Un `useEffect` qui rejoue ne doit
 * pas produire deux sessions d'une seule.
 */
export function startStudioSession(
  context: StudioContextType,
  options: { owner?: object | null; pieceId?: string; exerciseId?: string } = {},
): void {
  const owner = options.owner ?? null;

  if (
    current !== null &&
    current.context === context &&
    current.owner === owner &&
    current.pieceId === options.pieceId &&
    current.exerciseId === options.exerciseId
  ) {
    return;
  }

  // Fermer avant de mémoriser : la fermeture oublie le sujet, et l'ordre
  // inverse jetterait celui qu'on vient de poser.
  endStudioSession("switched");
  departure.opened({ context, options });

  current = {
    context,
    pieceId: options.pieceId,
    exerciseId: options.exerciseId,
    owner,
    startedAt: Date.now(),
    plays: 0,
    playedMs: 0,
    playingSince: 0,
    notesPlayed: 0,
    midiUsed: false,
    tempoChanges: 0,
    lastTempoChangeAt: 0,
    seeks: 0,
    sectionPicks: 0,
    loops: 0,
    metronomeUsed: false,
    countIns: 0,
  };
}

/**
 * Ferme la session en cours, s'il y en a une.
 *
 * Une session sans le moindre geste n'est pas émise : le Studio se monte aussi
 * derrière la bibliothèque et les parcours, et compter ces montages ferait de
 * `studio_session_ended` un compteur de navigations.
 */
export function endStudioSession(reason: "switched" | "navigation" | "pagehide"): void {
  if (current === null) {
    return;
  }

  const session = current;
  current = null;
  departure.closed();

  const durationMs = Date.now() - session.startedAt;
  const playedMs =
    session.playedMs +
    (session.playingSince === 0 ? 0 : Date.now() - session.playingSince);

  const idle =
    session.plays === 0 &&
    // Du son a été produit, même sans qu'aucun bouton n'ait été touché : c'est
    // le cas d'une séance reprise après un basculement d'application, où le
    // transport tournait déjà. Une session qui a sonné n'est pas une session
    // vide, et l'écarter retirerait du dénominateur un usage bien réel.
    playedMs === 0 &&
    session.notesPlayed === 0 &&
    session.seeks === 0 &&
    session.sectionPicks === 0 &&
    session.tempoChanges === 0 &&
    session.loops === 0 &&
    !session.metronomeUsed;

  if (idle) {
    return;
  }

  emit({
    name: "studio_session_ended",
    properties: {
      context: session.context,
      pieceId: session.pieceId,
      exerciseId: session.exerciseId,
      durationMs,
      plays: session.plays,
      playedMs: Math.round(playedMs),
      notesPlayed: session.notesPlayed,
      midiUsed: session.midiUsed,
      tempoChanges: session.tempoChanges,
      seeks: session.seeks,
      sectionPicks: session.sectionPicks,
      loops: session.loops,
      metronomeUsed: session.metronomeUsed,
      countIns: session.countIns,
      reason,
    },
  });
}

/** Ferme la session si le morceau actif n'est plus le sien. */
export function endStudioSessionUnlessOwnedBy(owner: object | null): void {
  if (current !== null && current.owner !== owner) {
    endStudioSession("switched");
  }
}

/** La session en cours porte-t-elle sur ce contexte ? Pour les tests et les garde-fous. */
export function currentStudioContext(): StudioContextType | null {
  return current?.context ?? null;
}

// ─── Compteurs ──────────────────────────────────────────────────────
//
// Tous silencieux hors session : un geste sans session ouverte est un geste
// d'une surface qui n'est pas le Studio, et il n'a pas à ouvrir de session
// derrière le dos de l'appelant.

/**
 * Une lecture démarrée **par la personne**, depuis une commande de transport.
 *
 * Ne touche pas au chronomètre, et c'est tout l'enjeu : `plays` compte des
 * intentions, `playedMs` compte du son. Les mélanger arrêtait le chronomètre
 * pour de bon au premier geste de travail — un clic de minimap, un glissé du
 * rouleau et un retour de boucle mettent tous le transport en pause puis le
 * relancent eux-mêmes, sans passer par aucun bouton. Le temps joué se serait
 * donc dégradé d'autant plus que la personne travaille, ce qui est exactement
 * le contraire de ce qu'il doit mesurer.
 */
export function recordPlay(): void {
  if (current !== null) {
    current.plays += 1;
  }
}

/**
 * Le transport s'est mis en marche, quelle qu'en soit la cause.
 *
 * Posé sur l'événement du transport et non sur la commande : c'est la seule
 * source qui voie les reprises menées par l'application elle-même.
 */
export function recordTransportStarted(): void {
  if (current !== null && current.playingSince === 0) {
    current.playingSince = Date.now();
  }
}

/** Le transport s'est arrêté, quelle qu'en soit la cause. */
export function recordPause(): void {
  if (current === null || current.playingSince === 0) {
    return;
  }

  current.playedMs += Date.now() - current.playingSince;
  current.playingSince = 0;
}

/**
 * Une note jouée par la personne.
 *
 * `midi` sépare le clavier matériel du reste : c'est le seul signal qui dise
 * qu'il y a un vrai piano au bout, et il n'était mesuré nulle part.
 */
export function recordNotePlayed(midi = false): void {
  if (current === null) {
    return;
  }

  current.notesPlayed += 1;

  if (midi) {
    current.midiUsed = true;
  }
}

/** Un changement de tempo, les rafales d'un même geste comptant pour une. */
export function recordTempoChange(): void {
  if (current === null) {
    return;
  }

  const now = Date.now();

  if (now - current.lastTempoChangeAt > TEMPO_BURST_MS) {
    current.tempoChanges += 1;
  }

  current.lastTempoChangeAt = now;
}

/** Un déplacement volontaire dans le morceau : glissé, molette ou minimap. */
export function recordSeek(): void {
  if (current !== null) {
    current.seeks += 1;
  }
}

/** Une mesure ou une section choisie dans le panneau Structure. */
export function recordSectionPick(): void {
  if (current !== null) {
    current.sectionPicks += 1;
  }
}

/** Une plage de boucle armée. */
export function recordLoopArmed(): void {
  if (current !== null) {
    current.loops += 1;
  }
}

export function recordMetronomeUsed(): void {
  if (current !== null) {
    current.metronomeUsed = true;
  }
}

export function recordCountIn(): void {
  if (current !== null) {
    current.countIns += 1;
  }
}

/** Remet le module à zéro. Réservé aux tests. */
export function resetStudioSessionForTests(): void {
  current = null;
  departure.reset();
}
